import type { AppState, CountryStats } from "./types";

// Pure mutators on AppState. Always return a new object rather than mutating
// — callers can decide whether to persist immediately.

const MAX_LEVEL = 5;
const MIN_LEVEL = 0;

const emptyStats = (iso: string, now: number): CountryStats => ({
  iso,
  level: 0,
  consecutiveCorrect: 0,
  consecutiveWrong: 0,
  lastSeen: now,
  totalCorrect: 0,
  totalWrong: 0,
});

export const getOrInitStats = (
  state: AppState,
  iso: string,
  now: number,
): CountryStats => state.stats[iso] ?? emptyStats(iso, now);

export const recordCorrect = (
  state: AppState,
  iso: string,
  now: number,
): AppState => {
  const prev = getOrInitStats(state, iso, now);
  const next: CountryStats = {
    ...prev,
    level: Math.min(MAX_LEVEL, prev.level + 1),
    consecutiveCorrect: prev.consecutiveCorrect + 1,
    consecutiveWrong: 0,
    lastSeen: now,
    totalCorrect: prev.totalCorrect + 1,
  };
  return { ...state, stats: { ...state.stats, [iso]: next } };
};

export const recordWrong = (
  state: AppState,
  iso: string,
  now: number,
): AppState => {
  const prev = getOrInitStats(state, iso, now);
  const next: CountryStats = {
    ...prev,
    level: Math.max(MIN_LEVEL, prev.level - 1),
    consecutiveCorrect: 0,
    consecutiveWrong: prev.consecutiveWrong + 1,
    lastSeen: now,
    totalWrong: prev.totalWrong + 1,
  };
  return { ...state, stats: { ...state.stats, [iso]: next } };
};

// A region is cleared when every country in it has been answered correctly
// at least once. Pure check — caller chooses what to do with the result.
export const isRegionCleared = (
  state: AppState,
  regionIsos: string[],
): boolean => {
  if (regionIsos.length === 0) return false;
  for (const iso of regionIsos) {
    const s = state.stats[iso];
    if (!s || s.totalCorrect < 1) return false;
  }
  return true;
};

export const markRegionCleared = (
  state: AppState,
  region: string,
  now: number,
): AppState => {
  const current = state.regionProgress[region];
  if (current?.cleared) return state;
  return {
    ...state,
    regionProgress: {
      ...state.regionProgress,
      [region]: { cleared: true, clearedAt: now },
    },
  };
};
